import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CreditCard,
  Check,
  X,
  Crown,
  Users,
  Zap,
  FileText,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { useBilling } from '@hooks/useBilling';
import { useAuth } from '@hooks/useAuth';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import Alert from '@components/common/Alert';
import { formatCurrency, formatDate } from '@utils/formatters';
import { clsx } from 'clsx';

const Billing = () => {
  const [searchParams] = useSearchParams();
  const { tenant } = useAuth();
  const {
    plans,
    subscription,
    isLoading,
    createCheckout,
    openPortal,
    cancelSubscription,
    reactivateSubscription,
    isCheckingOut,
    isOpeningPortal,
    isCanceling,
    isReactivating,
  } = useBilling();

  const [billingInterval, setBillingInterval] = useState('monthly');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  // Verificar se voltou do checkout com sucesso
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setShowSuccessAlert(true);
      // Limpar URL
      window.history.replaceState({}, '', '/app/settings/billing');
    }
  }, [searchParams]);

  const handleUpgrade = async (planId) => {
    await createCheckout({ plan: planId, interval: billingInterval });
  };

  const handleOpenPortal = async () => {
    await openPortal();
  };

  const handleCancel = async () => {
    await cancelSubscription();
    setShowCancelModal(false);
  };

  const handleReactivate = async () => {
    await reactivateSubscription();
  };

  if (isLoading) {
    return <Loading text="Carregando informações de billing..." />;
  }

  const currentPlan = tenant?.plan || 'free';
  const isSubscribed = currentPlan !== 'free';
  const isCanceling_ = subscription?.subscription?.cancelAtPeriodEnd;

  const PlanCard = ({ plan, isCurrent }) => {
    const price = billingInterval === 'monthly' 
      ? plan.priceFormatted.monthly 
      : plan.priceFormatted.yearlyPerMonth;
    
    const isPopular = plan.id === 'pro';
    const isFree = plan.id === 'free';

    return (
      <Card
        padding="none"
        className={clsx(
          'relative overflow-hidden transition-all',
          isPopular && 'ring-2 ring-primary-500 scale-105',
          isCurrent && 'bg-primary-50 dark:bg-primary-900/20'
        )}
      >
        {isPopular && (
          <div className="absolute top-0 right-0 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            POPULAR
          </div>
        )}

        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            {plan.id === 'pro' && <Zap className="text-yellow-500" size={20} />}
            {plan.id === 'family' && <Users className="text-blue-500" size={20} />}
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {plan.name}
            </h3>
          </div>

          <div className="mb-4">
            {isFree ? (
              <p className="text-3xl font-bold text-gray-900 dark:text-white">Grátis</p>
            ) : (
              <>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {price}
                  <span className="text-sm font-normal text-gray-500">/mês</span>
                </p>
                {billingInterval === 'yearly' && (
                  <p className="text-sm text-green-600">
                    Economia de ~17% no plano anual
                  </p>
                )}
              </>
            )}
          </div>

          <ul className="space-y-3 mb-6">
            {plan.features.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm">
                <Check className="text-green-500 flex-shrink-0" size={16} />
                <span className="text-gray-600 dark:text-gray-300">{feature}</span>
              </li>
            ))}
          </ul>

          {isCurrent ? (
            <Button fullWidth variant="secondary" disabled>
              <Check size={18} className="mr-2" />
              Plano Atual
            </Button>
          ) : isFree ? (
            <Button fullWidth variant="ghost" disabled>
              Plano Básico
            </Button>
          ) : (
            <Button
              fullWidth
              variant={isPopular ? 'primary' : 'outline'}
              onClick={() => handleUpgrade(plan.id)}
              isLoading={isCheckingOut}
            >
              <Crown size={18} className="mr-2" />
              {isSubscribed ? 'Trocar Plano' : 'Assinar Agora'}
            </Button>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div>
      <PageHeader
        title="Assinatura"
        subtitle="Gerencie seu plano e método de pagamento"
      />

      {/* Success Alert */}
      {showSuccessAlert && (
        <Alert
          variant="success"
          title="Assinatura ativada com sucesso!"
          className="mb-6"
          onClose={() => setShowSuccessAlert(false)}
        >
          Bem-vindo ao plano {currentPlan}! Todas as funcionalidades já estão disponíveis.
        </Alert>
      )}

      {/* Canceling Alert */}
      {isCanceling_ && (
        <Alert
          variant="warning"
          title="Assinatura será cancelada"
          className="mb-6"
        >
          Sua assinatura será cancelada em {formatDate(subscription?.subscription?.currentPeriodEnd)}.
          Você pode reativar a qualquer momento antes dessa data.
          <Button
            size="sm"
            variant="warning"
            className="mt-2"
            onClick={handleReactivate}
            isLoading={isReactivating}
          >
            Reativar Assinatura
          </Button>
        </Alert>
      )}

      {/* Current Subscription Card */}
      {isSubscribed && subscription && (
        <Card className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Plano {plans.find(p => p.id === currentPlan)?.name}
                </h3>
                <Badge variant={isCanceling_ ? 'warning' : 'success'}>
                  {isCanceling_ ? 'Cancelando' : 'Ativo'}
                </Badge>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {isCanceling_ 
                  ? `Acesso até ${formatDate(subscription?.subscription?.currentPeriodEnd)}`
                  : `Próxima cobrança em ${formatDate(subscription?.subscription?.currentPeriodEnd)}`
                }
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={handleOpenPortal}
                isLoading={isOpeningPortal}
                leftIcon={<CreditCard size={18} />}
              >
                Gerenciar Pagamento
              </Button>
              {!isCanceling_ && (
                <Button
                  variant="ghost"
                  onClick={() => setShowCancelModal(true)}
                >
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Billing Interval Toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex items-center gap-4 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-all',
              billingInterval === 'monthly'
                ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Mensal
          </button>
          <button
            onClick={() => setBillingInterval('yearly')}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2',
              billingInterval === 'yearly'
                ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-white'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Anual
            <Badge variant="success" size="sm">-17%</Badge>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isCurrent={plan.id === currentPlan}
          />
        ))}
      </div>

      {/* Invoice History */}
      {subscription?.invoices?.length > 0 && (
        <Card title="Histórico de Faturas" className="mb-8">
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {subscription.invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="py-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="text-gray-400" size={20} />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(invoice.amount / 100)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(invoice.paidAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={invoice.status === 'paid' ? 'success' : 'warning'}>
                    {invoice.status === 'paid' ? 'Pago' : 'Pendente'}
                  </Badge>
                  {invoice.invoicePdf && (
                    <a
                      href={invoice.invoicePdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ExternalLink size={16} className="text-gray-400" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cancel Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancelar Assinatura"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
              Manter Assinatura
            </Button>
            <Button
              variant="danger"
              onClick={handleCancel}
              isLoading={isCanceling}
            >
              Confirmar Cancelamento
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-gray-600 dark:text-gray-400">
                Ao cancelar, você perderá acesso a:
              </p>
              <ul className="mt-2 space-y-1 text-sm text-gray-500">
                <li>• Relatórios completos</li>
                <li>• Simulação financeira</li>
                <li>• Investimentos</li>
                <li>• Contas e categorias ilimitadas</li>
              </ul>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Sua assinatura continuará ativa até o final do período atual.
            Você pode reativar a qualquer momento.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Billing;
